import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import AcademyNav from '../../components/academy/AcademyNav';
import ArticleCard from '../../components/academy/ArticleCard';
import CtaBand from '../../components/academy/CtaBand';
import Seo from '../../components/academy/Seo';
import BrandHero from '../../components/BrandHero';
import { getCategories, getArticles } from '../../lib/academyApi';

const AcademyCategory = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [data, setData] = useState({ articles: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(1);
    getCategories().then((cats) => setCategory(cats.find((c) => c.slug === slug) || null));
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    setData({ articles: [], total: 0, page: 1, pages: 1 });
    getArticles({ category: slug, page, limit: 9 })
      .then(setData)
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug, page]);

  const name = category?.name || slug;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <Seo
        title={name}
        description={category?.description || `CarLens Academy articles about ${name}.`}
        path={`/academy/category/${slug}`}
        image={category?.image}
        breadcrumbs={[{ name: 'Academy', path: '/academy' }, { name, path: `/academy/category/${slug}` }]}
      />
      <BrandHero />
      <AcademyNav />

      {/* Category header */}
      <section className="relative overflow-hidden border-b border-zinc-800/70">
        {category?.image && (
          <>
            <img src={category.image} alt={name} className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e10] via-[#0e0e10]/80 to-[#0e0e10]/50" />
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="flex items-center gap-2 text-xs text-zinc-400 mb-4">
            <Link to="/academy" className="hover:text-white">Academy</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-zinc-300">{name}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {name}
          </h1>
          {category?.description && (
            <p className="mt-4 text-lg text-zinc-300 max-w-2xl leading-relaxed">{category.description}</p>
          )}
          <p className="mt-3 text-sm text-zinc-500">{data.total} {data.total === 1 ? 'article' : 'articles'}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-14 space-y-14">
        {loading ? (
          <p className="text-zinc-500 text-center py-20">Loading…</p>
        ) : data.articles.length === 0 ? (
          <p className="text-zinc-400 text-center py-20">No articles here yet — check back soon.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.articles.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>

            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-2" data-testid="category-pagination">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-zinc-700 text-sm text-zinc-300 disabled:opacity-40 hover:border-zinc-500 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <span className="text-sm text-zinc-400 px-2">Page {data.page} of {data.pages}</span>
                <button
                  disabled={page >= data.pages}
                  onClick={() => setPage((p) => p + 1)}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-zinc-700 text-sm text-zinc-300 disabled:opacity-40 hover:border-zinc-500 transition-colors"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

        <CtaBand />
      </div>
    </div>
  );
};

export default AcademyCategory;
